import {Injectable} from '@angular/core';
import {environment} from '../../environments/environment';
import {HttpClient, HttpParams} from '@angular/common/http';
import {finalize, map, Observable, shareReplay, Subscription, take, timer} from 'rxjs';
import {fetchAllPages} from '../_utils/http-params';
import {
    Badge,
    BadgeResponse,
    LeaderboardUser,
    NewBadge,
    NewStreak,
    Streak,
    StreakResponse,
    UserPoints
} from '../_types/rewards';
import {jsonToFormData} from '../_utils/form-data-builder';
import {GenericResponse} from '../_types/shared';
import {endOfDay, endOfMonth, startOfMonth} from "../_utils/date";

@Injectable({
    providedIn: 'root'
})
export class RewardsService {
    private api: string = `${environment.apiUrl}/rewards`;
    private cacheUserPoints$: Map<string, Observable<UserPoints>> = new Map();


    private cacheCurrentUserBadges$: Observable<Badge[]> | null = null;
    private cacheUserBadges$: Map<number, Observable<Badge[]>> = new Map();
    private cacheBadges$: Observable<Badge[]> | null = null;

    cacheCurrentUserStreaks$: Observable<Streak[]> | null = null;
    cacheUserStreaks$: Map<number, Observable<Streak[]>> = new Map();
    cacheStreaks$: Observable<Streak[]> | null = null;

    constructor(private http: HttpClient) {
    }


    // Badges

    getAllBadges(communityId: number): Observable<Badge[]> {
        if (!this.cacheBadges$) {
            const params: HttpParams = new HttpParams().set('community_id', communityId);
            this.cacheBadges$ = fetchAllPages<Badge>(this.http, `${this.api}/badges/`, 1000, params).pipe(
                finalize((): null => this.cacheBadges$ = null),
                shareReplay({bufferSize: 1, refCount: true, windowTime: 1000}),
            );
        }
        return this.cacheBadges$;
    }

    getCurrentUserBadges(communityId: number): Observable<Badge[]> {
        if (!this.cacheCurrentUserBadges$) {
            const params: HttpParams = new HttpParams().set('community_id', communityId);
            this.cacheCurrentUserBadges$ = fetchAllPages<BadgeResponse>(this.http, `${this.api}/user-badges/my-badges/`, 1000, params).pipe(
                finalize((): null => this.cacheCurrentUserBadges$ = null),
                shareReplay({bufferSize: 1, refCount: true, windowTime: 1000}),
                map((badgeResponse: BadgeResponse[]) =>
                    badgeResponse.map(res => ({
                        ...res.badge,
                        stack_count: res?.stack_count
                    }))
                )
            );
        }
        return this.cacheCurrentUserBadges$;
    }

    getUserBadges(communityId: number, userId: number): Observable<Badge[]> {
        if (!this.cacheUserBadges$.has(userId)) {
            const params: HttpParams = new HttpParams().append('user_id', userId).append('community_id', communityId);
            const userBadges$: Observable<Badge[]> = fetchAllPages<BadgeResponse>(this.http, `${this.api}/user-badges/earned-by/`, 1000, params).pipe(
                finalize((): boolean => this.cacheUserBadges$.delete(userId)),
                shareReplay({bufferSize: 1, refCount: true, windowTime: 1000}),
                map((badgeResponse: BadgeResponse[]) =>
                    badgeResponse.map(res => ({
                        ...res.badge,
                        stack_count: res?.stack_count
                    }))
                )
            );
            this.cacheUserBadges$.set(userId, userBadges$);
        }
        return this.cacheUserBadges$.get(userId)!;
    }



    newBadge(data: NewBadge, file: any): Observable<Badge> {
        const headerAttachmentNameValue: string = 'attachment; filename="image"';
        const headers = {
            'Content-Disposition': headerAttachmentNameValue,
        };
        const formData: FormData = jsonToFormData(data);
        if (file) {
            formData.append('image', file);
        }
        return this.http.post<Badge>(`${this.api}/badges/`, formData, {
            headers
        });
    }

    newBadgeRequirement(body: any): Observable<any> {
        return this.http.post<void>(`${environment.apiUrl}/rewards/badge-requirements/`, body);
    }

    getBadgeRequirements(id: number): Observable<any> {
        return this.http.get<void>(`${environment.apiUrl}/rewards/badge-requirements/${id}/`);
    }

    deleteBadgeRequirement(id: number): Observable<any> {
        return this.http.delete<void>(`${environment.apiUrl}/rewards/badge-requirements/${id}/`);
    }

    updateBadge(badgeId: number, data: NewBadge, file: any): Observable<Badge> {
        const headerAttachmentNameValue: string = 'attachment; filename="image"';
        const headers = {
            'Content-Disposition': headerAttachmentNameValue,
        };

        const fd: FormData = jsonToFormData(data);
        if (file) {
            fd.append('image', file);
        }
        return this.http.patch<any>(`${this.api}/badges/${badgeId}/`, fd, {
            headers
        });
    }

    removeBadge(badgeId: number): Observable<void> {
        return this.http.delete<void>(`${this.api}/badges/${badgeId}/`);
    }

    // Points

    getUserPoints(communityId: number, userId?: number, startDate?: string, endDate?: string): Observable<UserPoints> {
        const id = `${communityId}_${userId}_${startDate}_${endDate}`;
        if (!this.cacheUserPoints$.has(id)) {
            let params: HttpParams = new HttpParams().set('community_id', communityId);
            if (userId) {
                params = params.append('user_id', userId);
            }
            if (startDate) {
                params = params.append('start_date', startDate);
            }
            if (endDate) {
                params = params.append('end_date', endDate);
            }
            const userPoints$: Observable<UserPoints> = this.http.get<GenericResponse<UserPoints>>(`${this.api}/points/`, {params}).pipe(
                finalize((): Subscription => timer(1000).pipe(take(1)).subscribe(() => this.cacheUserPoints$.delete(id))),
                shareReplay({bufferSize: 1, refCount: true, windowTime: 1000}),
                map(response => response.data)
            );
            this.cacheUserPoints$.set(id, userPoints$);
        }
        return this.cacheUserPoints$.get(id)!;
    }

    getLeaderboardForAllTime(communityId: number): Observable<LeaderboardUser[]> {
        const params: HttpParams = new HttpParams().set('community_id', communityId);
        return this.http.get<GenericResponse<LeaderboardUser[]>>(`${this.api}/leaderboard/`, {params})
            .pipe(map(res => res.data));
    }

    getLeaderboardForLastYear(communityId: number): Observable<LeaderboardUser[]> {
        const currentDate = new Date();
        let endDate: string | undefined = this.formatDateToYYYYMMDD(new Date());
        const previousYearDate = new Date(currentDate);
        previousYearDate.setFullYear(currentDate.getFullYear() - 1);
        let startDate: string | undefined = this.formatDateToYYYYMMDD(endOfDay(previousYearDate)!);
        let params: HttpParams = new HttpParams().set('community_id', communityId);
        if (startDate) {
            params = params.append('start_date', startDate)
        }
        if (endDate) {
            params = params.append('end_date', endDate)
        }
        return this.http.get<GenericResponse<LeaderboardUser[]>>(`${this.api}/leaderboard/`, {params})
            .pipe(map(res => res.data));
    }

    getLeaderboardMonthly(communityId: number, month?: { year: number, month: number }): Observable<LeaderboardUser[]> {
        let startDate: string | undefined = this.formatDateToYYYYMMDD(startOfMonth(new Date())!);
        let endDate: string | undefined = this.formatDateToYYYYMMDD(endOfMonth(new Date())!);

        let params: HttpParams = new HttpParams().set('community_id', communityId);
        if (startDate) {
            params = params.append('start_date', startDate)
        }
        if (endDate) {
            params = params.append('end_date', endDate)
        }
        return this.http.get<GenericResponse<LeaderboardUser[]>>(`${this.api}/leaderboard/`, {params})
            .pipe(map(res => res.data));
    }

    private formatDateToYYYYMMDD(date: Date) {
        if (!date) return undefined;
        const year: number = date.getFullYear();
        const month: string = String(date.getMonth() + 1).padStart(2, '0'); // getMonth() returns 0-11, so add 1
        const day: string = String(date.getDate()).padStart(2, '0');

        return `${year}-${month}-${day}`;
    }



    // streaks
    getAllStreaks(communityId: number): Observable<Streak[]> {
        if (!this.cacheStreaks$) {
            const params: HttpParams = new HttpParams().set('community_id', communityId);
            this.cacheStreaks$ = fetchAllPages<Streak>(this.http, `${this.api}/streaks/`, 1000, params).pipe(
                finalize((): null => this.cacheStreaks$ = null),
                shareReplay({bufferSize: 1, refCount: true, windowTime: 1000}),
            );
        }
        return this.cacheStreaks$;
    }

    getCurrentUserStreaks(communityId: number): Observable<Streak[]> {
        if (!this.cacheCurrentUserStreaks$) {
            const params: HttpParams = new HttpParams().set('community_id', communityId);
            this.cacheCurrentUserStreaks$ = fetchAllPages<StreakResponse>(this.http, `${this.api}/user-streaks/my-streaks/`, 1000, params).pipe(
                finalize((): null => this.cacheCurrentUserStreaks$ = null),
                shareReplay({bufferSize: 1, refCount: true, windowTime: 1000}),
                map((streakResponse: StreakResponse[]) =>
                    streakResponse.map(res => ({
                        ...res.streak,
                        current_streak_length: res?.current_streak_length
                    }))
                )
            );
        }
        return this.cacheCurrentUserStreaks$;
    }

    getUserStreaks(communityId: number, userId: number): Observable<any> {
        if (!this.cacheUserStreaks$.has(userId)) {
            const params: HttpParams = new HttpParams().append('user_id', userId).append('community_id', communityId);
            const userStreaks$: Observable<Streak[]> = fetchAllPages<StreakResponse>(this.http, `${this.api}/user-streaks/earned-by/`, 1000, params).pipe(
                finalize((): boolean => this.cacheUserStreaks$.delete(userId)),
                shareReplay({bufferSize: 1, refCount: true, windowTime: 1000}),
                map((streakResponse: StreakResponse[]) =>
                    streakResponse.map(res => ({
                        ...res.streak,
                        current_streak_length: res?.current_streak_length
                    }))
                )
            );
            this.cacheUserStreaks$.set(userId, userStreaks$);
        }
        return this.cacheUserStreaks$.get(userId)!;
    }


    newStreak(data: NewStreak, file: any): Observable<Streak> {
        const headerAttachmentNameValue: string = 'attachment; filename="image"';
        const headers = {
            'Content-Disposition': headerAttachmentNameValue,
        };
        const formData: FormData = jsonToFormData(data);
        if (file) {
            formData.append('image', file);
        }
        return this.http.post<Streak>(`${this.api}/streaks/`, formData, {
            headers
        });
    }

    newStreakRequirement(body: any): Observable<any> {
        return this.http.post<void>(`${environment.apiUrl}/rewards/streak-requirements/`, body);
    }

    getStreakRequirements(id: number): Observable<any> {
        return this.http.get<void>(`${environment.apiUrl}/rewards/streak-requirements/${id}/`);
    }

    deleteStreakRequirement(id: number): Observable<any> {
        return this.http.delete<void>(`${environment.apiUrl}/rewards/streak-requirements/${id}/`);
    }

    updateStreak(streakId: number, data: NewStreak, file: any): Observable<Streak> {
        const headerAttachmentNameValue: string = 'attachment; filename="image"';
        const headers = {
            'Content-Disposition': headerAttachmentNameValue,
        };

        const fd: FormData = jsonToFormData(data);
        if (file) {
            fd.append('image', file);
        }
        return this.http.patch<any>(`${this.api}/streaks/${streakId}/`, fd, {
            headers
        });
    }

    removeStreak(streakId: number): Observable<void> {
        return this.http.delete<void>(`${this.api}/streaks/${streakId}/`);
    }
}
